import { ExternalLink } from 'lucide-react';
import type { DisclosureListItem, DisclosureReviewStatus } from './disclosures.types';

// 권위: ARCH_DASHBOARD §4.6 탭 6-A 목록 테이블.
// 컬럼: created_at (KST) | symbol + corp_name (2줄) | disclosure_title (ellipsis)
//       | event_type (배지) | review_status (배지) | ex_date | adjustment_factor | confidence | DART 링크

interface Props {
  items: DisclosureListItem[];
  isLoading: boolean;
  onRowClick: (item: DisclosureListItem) => void;
}

export function DisclosuresTable({ items, isLoading, onRowClick }: Props): JSX.Element {
  return (
    <div className="syslog-table-wrap disclosure-table-wrap">
      <table className="syslog-table disclosure-table">
        <thead>
          <tr>
            <th className="syslog-col-time">접수 시각 (KST)</th>
            <th className="disclosure-col-symbol">종목</th>
            <th className="disclosure-col-title">공시 제목</th>
            <th className="disclosure-col-event">이벤트</th>
            <th className="disclosure-col-status">Review</th>
            <th className="disclosure-col-ex">Ex-date</th>
            <th className="disclosure-col-factor">Factor</th>
            <th className="disclosure-col-conf">Conf.</th>
            <th className="disclosure-col-link" aria-label="DART 원문 링크" />
          </tr>
        </thead>
        <tbody>
          {isLoading && items.length === 0 && (
            <tr>
              <td colSpan={9} className="syslog-empty">
                불러오는 중…
              </td>
            </tr>
          )}
          {!isLoading && items.length === 0 && (
            <tr>
              <td colSpan={9} className="syslog-empty">
                조건에 맞는 공시가 없습니다.
              </td>
            </tr>
          )}
          {items.map((it) => {
            const title = it.disclosureTitle ?? '(제목 없음)';
            return (
              <tr
                key={it.auditId}
                className="disclosure-row"
                onClick={() => onRowClick(it)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick(it);
                  }
                }}
              >
                <td className="syslog-col-time">{formatTs(it.createdAt)}</td>
                <td className="disclosure-col-symbol">
                  <div className="disclosure-symbol__code">{it.symbol ?? '-'}</div>
                  <div className="disclosure-symbol__name" title={it.corpName ?? ''}>
                    {it.corpName ?? <span className="syslog-muted">-</span>}
                  </div>
                </td>
                <td className="disclosure-col-title" title={title}>
                  {title}
                </td>
                <td className="disclosure-col-event">
                  {it.eventType ? (
                    <span className="disclosure-evt-badge">{it.eventType}</span>
                  ) : (
                    <span className="syslog-muted">-</span>
                  )}
                </td>
                <td className="disclosure-col-status">
                  <ReviewStatusBadge status={it.reviewStatus} />
                </td>
                <td className="disclosure-col-ex">
                  {it.exDate ?? <span className="syslog-muted">-</span>}
                </td>
                <td className="disclosure-col-factor">
                  {formatFactor(it.adjustmentFactor)}
                </td>
                <td className="disclosure-col-conf">
                  {formatConfidence(it.confidence)}
                </td>
                <td className="disclosure-col-link">
                  {it.disclosureUrl ? (
                    <a
                      href={it.disclosureUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      aria-label="DART 원문 열기"
                      className="disclosure-link"
                    >
                      <ExternalLink size={14} aria-hidden="true" />
                    </a>
                  ) : (
                    <span className="syslog-muted">-</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ReviewStatusBadge({
  status,
}: {
  status: DisclosureReviewStatus;
}): JSX.Element {
  // pending=회색, approved=녹색, rejected=빨강, auto_accepted=파랑 (스펙).
  // 'edited' 는 스펙 외이지만 백엔드가 반환할 수 있으므로 회색 계열로 폴백.
  const cls = `disclosure-status-badge disclosure-status-badge--${normalize(status)}`;
  return <span className={cls}>{status}</span>;
}

function normalize(s: string): string {
  if (s === 'pending_review') return 'pending';
  if (s === 'auto_accepted') return 'auto';
  if (s === 'approved') return 'approved';
  if (s === 'rejected') return 'rejected';
  return 'other';
}

function formatTs(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

function formatFactor(v: number | null): JSX.Element | string {
  if (v == null || !Number.isFinite(v)) return <span className="syslog-muted">-</span>;
  return v.toFixed(6);
}

function formatConfidence(v: number | null): JSX.Element | string {
  if (v == null || !Number.isFinite(v)) return <span className="syslog-muted">-</span>;
  return v.toFixed(2);
}
